import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import apiFetch from "../../../shared/services/apiClient";

export default function PortalAvisos() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [avisos,   setAvisos]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);

  function load() {
    apiFetch("/api/core/portal/avisos/")
      .then(r => r.json())
      .then(d => setAvisos(d.results || d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // Si llega un aviso nuevo del condominio mientras estamos viendo esta
  // pantalla, se actualiza sola en vez de esperar a que alguien la recargue.
  useEffect(() => {
    function onDataChanged(e) {
      if (e.detail?.tipo === "AVISO") load();
    }
    window.addEventListener("portal:data-changed", onDataChanged);
    return () => window.removeEventListener("portal:data-changed", onDataChanged);
  }, []);

  function openAviso(aviso) {
    setSelected(aviso);
    if (!aviso.leido) {
      apiFetch(`/api/core/portal/avisos/${aviso.id}/`).catch(() => {});
      setAvisos(prev => prev.map(a => a.id === aviso.id ? { ...a, leido: true } : a));
    }
  }

  const unreadCount = avisos.filter(a => !a.leido).length;

  // Hero theme vars — sky/blue palette for notifications
  const heroBg    = isLight ? "linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 50%,#f0fdfa 100%)" : "linear-gradient(135deg,#07101c 0%,#0f172a 60%,#071220 100%)";
  const heroText  = isLight ? "#0f172a" : "#ffffff";
  const heroSub   = isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.45)";
  const heroBorder= isLight ? "rgba(56,189,248,0.3)" : "rgba(56,189,248,0.18)";
  const heroShadow= isLight ? "0 4px 24px rgba(56,189,248,0.15),0 1px 3px rgba(0,0,0,0.06)" : "0 0 0 1px rgba(255,255,255,0.03),0 16px 48px rgba(0,0,0,.5)";

  // ── Detail view ──
  if (selected) {
    return (
      <>
        <style>{`@keyframes pavFadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}`}</style>
        <div style={{ padding:"28px 28px 48px", animation:"pavFadeUp .35s ease both" }}>
          <button onClick={() => setSelected(null)} style={{
            background:"var(--hover-bg)", border:"1px solid var(--border-card)", cursor:"pointer",
            color:"var(--text-secondary)", display:"inline-flex", alignItems:"center", gap:8,
            fontSize:13, fontWeight:700, marginBottom:24, padding:"8px 16px", borderRadius:9,
            transition:"all .15s",
          }}>
            <i className="fa-solid fa-arrow-left" /> {t("portal.volver")}
          </button>
          <div>
            <div style={{
              background:"var(--bg-card)", border:"1px solid var(--border-card)",
              borderRadius:16, overflow:"hidden",
              boxShadow: isLight?"0 4px 20px rgba(0,0,0,.08)":"0 4px 24px rgba(0,0,0,.3)",
            }}>
              {/* Detail header bar */}
              <div style={{
                padding:"20px 28px", borderBottom:"1px solid var(--border-card)",
                background: isLight?"linear-gradient(135deg,#f0f9ff,#fff)":"linear-gradient(135deg,#07101c,#0f172a)",
                display:"flex", alignItems:"center", gap:12,
              }}>
                <div style={{ width:40, height:40, borderRadius:11, background:"rgba(56,189,248,0.15)", border:"1px solid rgba(56,189,248,0.3)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <i className="fa-solid fa-bell" style={{ color:"#38bdf8", fontSize:16 }} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:"rgba(56,189,248,0.8)", fontWeight:700, textTransform:"uppercase", letterSpacing:1 }}>
                    {t("portal.avisos")}
                  </div>
                  <div style={{ fontSize:12, color:heroSub }}>{selected.created_at?.split("T")[0]}</div>
                </div>
              </div>
              <div style={{ padding:"28px 28px 32px" }}>
                <h2 style={{ margin:"0 0 20px", fontSize:"clamp(18px,2.5vw,24px)", fontWeight:800, color:"var(--text-primary)", lineHeight:1.25 }}>
                  {selected.titulo}
                </h2>
                <p style={{ margin:"0 0 24px", fontSize:15, lineHeight:1.8, color:"var(--text-primary)", whiteSpace:"pre-wrap" }}>
                  {selected.mensaje}
                </p>
                {selected.archivo_url && (
                  <a href={selected.archivo_url} target="_blank" rel="noreferrer" style={{
                    display:"inline-flex", alignItems:"center", gap:10,
                    background:"rgba(56,189,248,0.1)", border:"1px solid rgba(56,189,248,0.3)",
                    color:"#38bdf8", padding:"11px 18px", borderRadius:10,
                    textDecoration:"none", fontWeight:700, fontSize:14,
                  }}>
                    <i className="fa-solid fa-paperclip" /> {t("portal.verArchivo")}
                  </a>
                )}
                <div style={{ marginTop:28, paddingTop:16, borderTop:"1px solid var(--border-card)", display:"flex", alignItems:"center", gap:8 }}>
                  <i className="fa-solid fa-user-tie" style={{ color:"var(--text-secondary)", fontSize:12 }} />
                  <span style={{ fontSize:12, color:"var(--text-secondary)" }}>
                    {t("portal.enviadoPor")}: <strong style={{ color:"var(--text-primary)" }}>{selected.creado_por_nombre || "—"}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── List view ──
  return (
    <>
      <style>{`
        @keyframes pavFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        .pav-card { transition: transform .15s, box-shadow .15s, border-color .15s !important; }
        .pav-card:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 24px rgba(0,0,0,.14) !important; }
      `}</style>
      <div style={{ padding:"28px 28px 48px", animation:"pavFadeUp .4s ease both" }}>

        {/* ── Hero ── */}
        <div style={{
          borderRadius:16, marginBottom:24, position:"relative", overflow:"hidden",
          background:heroBg, border:`1px solid ${heroBorder}`, boxShadow:heroShadow,
        }}>
          <div style={{ position:"absolute", top:-50, right:-50, width:220, height:220, borderRadius:"50%", background:`radial-gradient(circle,${isLight?"rgba(56,189,248,0.1)":"rgba(56,189,248,0.14)"} 0%,transparent 70%)`, pointerEvents:"none" }} />
          <div style={{ position:"relative", padding:"24px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:16 }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ width:32, height:32, borderRadius:9, flexShrink:0, background:"rgba(56,189,248,0.15)", border:"1px solid rgba(56,189,248,0.35)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-bell" style={{ color:"#38bdf8", fontSize:14 }} />
              </div>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color:"#38bdf8", letterSpacing:0.5, display:"block", marginBottom:6 }}>{t("portal.avisos")}</span>
                <h1 style={{ fontSize:"clamp(18px,2.5vw,26px)", fontWeight:800, color:heroText, margin:"0 0 4px" }}>{t("portal.avisos")}</h1>
                <p style={{ margin:0, fontSize:13, color:heroSub }}>{t("portal.avisosDesc")}</p>
              </div>
            </div>
            {unreadCount > 0 && (
              <div style={{
                background:"rgba(56,189,248,0.1)", border:"1px solid rgba(56,189,248,0.35)",
                borderRadius:12, padding:"12px 20px", textAlign:"center",
              }}>
                <div style={{ fontSize:28, fontWeight:900, color:"#38bdf8", lineHeight:1 }}>{unreadCount}</div>
                <div style={{ fontSize:11, color:heroSub, fontWeight:600, marginTop:2, textTransform:"uppercase", letterSpacing:1 }}>
                  {t("portal.sinLeer") || "Sin leer"}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── List ── */}
        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[...Array(4)].map((_,i) => (
              <div key={i} style={{ background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14, padding:"18px 20px" }}>
                <div style={{ display:"flex", gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:"var(--hover-bg)", flexShrink:0 }} />
                  <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ height:14, borderRadius:5, background:"var(--hover-bg)", width:"60%" }} />
                    <div style={{ height:11, borderRadius:5, background:"var(--hover-bg)", width:"40%" }} />
                    <div style={{ height:11, borderRadius:5, background:"var(--hover-bg)", width:"80%" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : avisos.length === 0 ? (
          <div style={{ textAlign:"center", color:"var(--text-secondary)", padding:56, background:"var(--bg-card)", borderRadius:16, border:"1px solid var(--border-card)" }}>
            <i className="fa-solid fa-bell-slash" style={{ fontSize:40, opacity:.3, display:"block", marginBottom:12 }} />
            <p style={{ margin:0, fontSize:14 }}>{t("portal.sinAvisos")}</p>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {avisos.map(a => (
              <div key={a.id} className="pav-card" onClick={() => openAviso(a)} style={{
                background:"var(--bg-card)", borderRadius:14, padding:"18px 20px",
                border:`1px solid ${a.leido ? "var(--border-card)" : "rgba(56,189,248,0.5)"}`,
                boxShadow: a.leido
                  ? (isLight?"0 2px 8px rgba(0,0,0,.05)":"0 2px 10px rgba(0,0,0,.2)")
                  : "0 0 0 2px rgba(56,189,248,0.15)",
                cursor:"pointer", position:"relative",
              }}>
                <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                  {/* Icon */}
                  <div style={{
                    width:42, height:42, borderRadius:11, flexShrink:0,
                    background: a.leido ? "var(--hover-bg)" : "rgba(56,189,248,0.12)",
                    border: `1px solid ${a.leido?"var(--border-card)":"rgba(56,189,248,0.3)"}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                  }}>
                    <i className="fa-solid fa-bell" style={{ color:a.leido?"var(--text-secondary)":"#38bdf8", fontSize:16 }} />
                  </div>
                  {/* Content */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4, gap:8 }}>
                      <h3 style={{ margin:0, fontSize:15, fontWeight:a.leido?600:800, color:"var(--text-primary)", lineHeight:1.3 }}>
                        {a.titulo}
                      </h3>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                        {!a.leido && (
                          <span style={{ width:8, height:8, borderRadius:"50%", background:"#38bdf8", display:"inline-block", boxShadow:"0 0 8px rgba(56,189,248,0.6)" }} />
                        )}
                        <span style={{ fontSize:11, color:"var(--text-secondary)", whiteSpace:"nowrap" }}>{a.created_at?.split("T")[0]}</span>
                      </div>
                    </div>
                    <p style={{
                      margin:0, fontSize:13, color:"var(--text-secondary)", lineHeight:1.55,
                      display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden",
                    }}>
                      {a.mensaje}
                    </p>
                    {a.archivo_url && (
                      <div style={{ display:"inline-flex", alignItems:"center", gap:5, marginTop:8, fontSize:11, color:"#f97316", fontWeight:700 }}>
                        <i className="fa-solid fa-paperclip" /> {t("portal.tieneArchivo")}
                      </div>
                    )}
                  </div>
                </div>
                {/* Unread accent line */}
                {!a.leido && (
                  <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3, borderRadius:"14px 0 0 14px", background:"linear-gradient(180deg,#38bdf8,#818cf8)" }} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
